import type { KeyboardEvent, Ref } from "react";

import styles from "./Field.module.css";

interface TextAreaProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  maxLength?: number;
  hint?: string | null;
  error?: string | null;
  autoFocus?: boolean;
  /**
   * Shown, not editable — what was typed stays in view and can be selected,
   * but no longer changed (the handover dialog after a 409, R2-8).
   */
  readOnly?: boolean;
  textareaRef?: Ref<HTMLTextAreaElement>;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
}

/** `TextField`, several lines: a note, a reason, a handover message. */
export function TextArea({
  id,
  label,
  value,
  onChange,
  rows = 4,
  maxLength,
  hint,
  error,
  autoFocus,
  readOnly = false,
  textareaRef,
  onKeyDown,
}: TextAreaProps) {
  // Fix-Runde 1, M5 — see TextField: both stand, both are read.
  const describedBy = [error ? `${id}-error` : null, hint ? `${id}-hint` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={styles.field}>
      <label className={`label ${styles.fieldLabel}`} htmlFor={id}>
        {label}
      </label>
      <textarea
        ref={textareaRef}
        id={id}
        rows={rows}
        maxLength={maxLength}
        className={`${styles.input} ${styles.textarea} ${error ? styles.inputBad : ""} ${readOnly ? styles.inputReadOnly : ""}`}
        value={value}
        readOnly={readOnly}
        autoFocus={autoFocus}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
      />
      {error ? (
        <span id={`${id}-error`} className={`${styles.hint} ${styles.hintBad}`}>
          {error}
        </span>
      ) : null}
      {hint ? (
        <span id={`${id}-hint`} className={styles.hint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
